import moment from 'moment';
import XLSX from 'xlsx';
import type { Types as CoreTypes } from '../../core/types';
import { getVertexId } from '../../core/util';
import type { ObjectId } from '../../common/types';

type Schedule = {
  division: CoreTypes.Deep.Division;
  teachers: CoreTypes.Deep.Teacher[];
  courses:  CoreTypes.Deep.Course[];
  persons:  CoreTypes.Deep.Person[];
};

export default function (
  schedule: Schedule,
): XLSX.WorkBook {
  const did = getVertexId(schedule.division, { idKey: 'id' });
  if (!did) throw new Error('division id is undefined');

  // get start and end dates from division in format date + time format: 2024-08-15 00:00:00
  const startDate = moment.utc(schedule.division.start).format('YYYY-MM-DD HH:mm:ss');
  const endDate = moment.utc(schedule.division.end).format('YYYY-MM-DD HH:mm:ss');

  // Map persons by id to SSN
  const personMap = new Map<string, string>();
  schedule.persons.forEach((p) => {
    const ssn = p.SSN?.value;
    const id = getVertexId(p, { idKey: '_id' });
    if (ssn && id)
      personMap.set(id, ssn);
  });

  const teacherMap = new Map<string, string>();
  schedule.teachers.forEach((t) => {
    const signature = t.signature;
    const id = getVertexId(t, { idKey: 'id' });
    if (signature && id)
      teacherMap.set(id, signature);
  });



  const courses = schedule.courses.map((course) => {
    const group = validateGroup(course.groups);
    if (!group) return null;

    const teacherIds = course.teachers?.map((t) => getVertexId(t.to, { idKey: 'id' })).filter((id) => id != null) ?? [];
    const teacherSignatures = teacherIds.map((id) => teacherMap.get(id)).filter((sig) => sig != null);

    const courseName = course.displayName || '';
    const subject = course.subject || '';

    const membersSSNs = group.members.map((id) => {
      return personMap.get(id);
    }).filter((ssn) => !!ssn);

    if (membersSSNs.length === 0) return null;

    return membersSSNs.map((ssn) => ({
      Personnummer:       ssn ? formatSSN(ssn) : '',
      Kurskod:            subject,
      Benämning:          courseName,
      Undervisningsgrupp: group.displayName || '',
      Lärarsignatur:      teacherSignatures ? teacherSignatures.join(',') : '',
      Poäng:              0,
      Startdatum:         startDate,
      Slutdatum:          endDate
    }));

  }).flat().filter(x => x !== null);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(courses), 'Kurser');

  return wb;
}

// Validate that the group is valid and has at least one member
function validateGroup (groups: CoreTypes.Deep.Course['groups']): {
  members:      string[], // Array of member IDs
  displayName?: string
} | null {
  const group = groups?.at(0)?.to;

  if (!(typeof group === 'object') || !('members' in group)) return null;

  if (!group?.members || group.members.length === 0) return null;

  const memberIds: string[] = [];
  for (const member of group.members) {
    const id = (typeof member === 'object' && 'id' in member) ? member.id : member;
    memberIds.push((id as unknown as (ObjectId | string)).toString());
  }
  if (memberIds.length === 0) return null;

  return { members: memberIds, displayName: group.displayName };
}

/**
 * Formats a Swedish SSN (personnummer) from the standard 12-digit format (YYYYMMDDXXXX) to the common 10-digit format (YYMMDD-XXXX).
 * If the input is already in the 10-digit format, it is returned as is.
 * If the input is invalid, null is returned.
 *
 * @param ssn - The SSN to format, either as a string or number.
 * @returns The formatted SSN in 10-digit format, or null if invalid.
 */
function formatSSN (ssn: string | number): string {
  if (typeof ssn === 'number') {
    ssn = ssn.toString();
  }
  if (typeof ssn !== 'string') {
    return '';
  }

  // Check if already in YYMMDD-XXXX format
  const regex10 = /^\d{6}-\d{4}$/;
  if (regex10.test(ssn)) {
    return ssn;
  }

  // Remove any non-digit characters
  ssn = ssn.replace(/\D/g, '');

  if (ssn.length === 12) {
    // Convert from YYYYMMDDXXXX to YYMMDD-XXXX
    const year = ssn.slice(2, 4);
    const month = ssn.slice(4, 6);
    const day = ssn.slice(6, 8);
    const lastFour = ssn.slice(8);
    return `${year}${month}${day}-${lastFour}`;
  } else if (ssn.length === 10) {
    // Already in YYMMDDXXXX format, just add the hyphen
    const datePart = ssn.slice(0, 6);
    const lastFour = ssn.slice(6);
    return `${datePart}-${lastFour}`;
  } else {
    // Invalid SSN format
    return '';
  }
}